import * as React from 'react';
import gql from 'graphql-tag';
import { useEffect, useRef, useReducer } from 'react';
import {
    Box,
    Button,
    FormControl,
    Grid,
    Select,
    MenuItem,
    withStyles,
    WithStyles as IWithStyles,
} from '@material-ui/core';
import { withApollo } from 'react-apollo';
import { compose } from 'fp-ts/lib/function';
import { Lens } from 'monocle-ts';
import { IVideoListView, IQueryResponse, Sort } from './types';
import { withVideoLibraryState, IVideoLibraryState } from '../../State';
import { reducer, initialState } from './reducer';
import { withLocalState } from '../../../../App/LocalState';
import { VideoTile } from './Components/VideoTile';
import { VideoUploadDialog } from '../VideoUploadDialog';
import { ModalDialog } from '../ModalDialog';
import { withNavigation } from '../../../../App/withNavigation';
import { IClip } from '../../../../Assets/videoVideoLibrary';
import UploadIcon from './../../../../Assets/upload-new.png';
import KfLogo from './../../../../Assets/kflogonew.png';
import {
    gameFootageDeleteVideo,
    gameFootageEditVideo,
    gameFootageOpened,
    gameFootagePageUnmounted,
    gameFootageSort,
    gameFootageUploadOpened,
} from 'src/App/UserEvents';
import { sendUserEvent } from 'src/App/UserEvents/UserEventManager';
import { styles } from './styles';
import { useWindowSize } from './useWindowSize';
import services from 'src/Api/services';
import LogoutIcon from '../../../../Assets/logout.png';
import TutorialIcon from '../../../../Assets/Tutorial_icon.png';
import ContactUsIcon from '../../../../Assets/contact_us.png';
import { PlayArrow as PlayArrowIcon } from '@material-ui/icons';
import { PlayArrowIconNew } from 'src/Assets/svgs/PlayArrowIconNew';
import { ContactUsIconNew } from 'src/Assets/svgs/ContactUsIconNew';
import { LogoutIconNew } from 'src/Assets/svgs/LogoutIconNew';
import { DownArrowIcon } from 'src/Assets/svgs/DownArrowIcon';
import EditIcon from '../../../../Assets/edit-new.png';
import DeleteIcon from '../../../../Assets/delete-new-gray.png';
import ContactIcon from '../../../../Assets/contact-icon-new.png';
import { TRUE } from 'sass';

let license = false;
const alephURI = process.env.REACT_APP_ALEPH_URI;

const getQuery = ([field, direction]: Sort) => gql`
    query MyVideos ($limit: Int, $offset: Int, $email: String!) {
        video (limit: $limit, offset: $offset, where: {user_creator_email: {_eq: $email}}, order_by: {${field}: ${direction}}) {
            created_at
            subtitle
            duration
            id
            s3_key
            title
        }
    }
`;

const sorts = [
    {
        name: 'Sort by: Title (A - Z)',
        value: ['title', 'desc'],
    },
    {
        name: 'Sort by: Title (Z - A)',
        value: ['title', 'asc'],
    },
    {
        name: 'Sort by: Last Uploaded',
        value: ['created_at', 'desc'],
    },
    {
        name: 'Sort by: First Uploaded',
        value: ['created_at', 'asc'],
    },
    {
        name: 'Sort by: Largest',
        value: ['size', 'desc'],
    },
    {
        name: 'Sort by: Smalled',
        value: ['size', 'asc'],
    },
    {
        name: 'Sort by: Shortest',
        value: ['duration', 'asc'],
    },
    {
        name: 'Sort by: Longest',
        value: ['duration', 'desc'],
    },
];

const editVideoModalFields = [
    {
        label: 'Clip Name',
        name: 'title',
        required: true,
        placeHolder: 'Clip Name',
    },
    {
        label: 'Description',
        name: 'subtitle',
        required: false,
        placeHolder: 'Type here',
        type: 'textarea',
    },
];

const pagination = {
    limit: 12,
    offset: 0,
};

const VideoList = ({
    classes,
    history,
    client,
    videoLibraryStateMgr,
    localStateMgr,
}: IVideoListView & IWithStyles) => {
    const email = localStorage.getItem('username') as string;

    const { videoLibraryState, setVideoLibraryState } = videoLibraryStateMgr;
    const { selectedVideo } = videoLibraryState;
    const [state, dispatch] = useReducer(reducer, initialState);
    const videoListRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowSize()[0];
    const loadVideos = (
        action:
            | 'ON_LOAD_MORE_VIDEOS'
            | 'ON_LOAD_VIDEOS'
            | 'ON_ERROR'
            | 'ON_UPLOAD_VIDEO',
        limit: number,
        offset: number
    ) => {
        const query = getQuery(state.sort);

        client
            .query({
                query,
                variables: {
                    email,
                    limit,
                    offset,
                },
            })
            .then(({ data, error }: IQueryResponse) => {
                const payload = data.video || [];
                const type = error ? 'ON_ERROR' : action;

                dispatch({
                    type,
                    payload,
                });
            });
    };

    useEffect(() => {
        loadVideos('ON_LOAD_VIDEOS', pagination.limit, pagination.offset);
    }, [state.sort]);

    useEffect(() => {
        sendUserEvent(gameFootageOpened, window.location.href);
        return () => UserEventOnUnmount();
    }, []);

    useEffect(() => {
        handleToggleButton();
        window.addEventListener('resize', handleToggleButton);
        return () => window.removeEventListener('resize', handleToggleButton);
    }, []);

    function handleToggleButton() {
        if (window.innerWidth < 870) {
            return dispatch({
                type: 'ON_UPLOAD_BUTTON_TEXT_REMOVE',
                payload: false,
            });
        }
        return dispatch({ type: 'ON_UPLOAD_BUTTON_TEXT_ADD', payload: false });
    }

    const UserEventOnUnmount = () => {
        return history.location.pathname !== '/video-library/'
            ? sendUserEvent(gameFootagePageUnmounted, window.location.href)
            : undefined;
    };

    const openModal = (
        modalName: 'editVideoModal' | 'deleteVideoModal' | 'contactUsModal',
        clip: IClip
    ) => (e: React.MouseEvent) => {
        e.preventDefault();
        setVideoLibraryState(
            compose(
                Lens.fromProp<IVideoLibraryState>()('selectedVideo').set(clip),
                Lens.fromPath<IVideoLibraryState>()([
                    'modalsOpened',
                    modalName,
                ]).set(true)
            )(videoLibraryState)
        );
    };

    const closeModal = (modalName: string) => {
        setVideoLibraryState(
            Lens.fromPath<IVideoLibraryState>()([
                'modalsOpened',
                modalName,
            ]).set(false)(videoLibraryState)
        );
    };

    const deleteVideo = async () => {
        sendUserEvent(
            gameFootageDeleteVideo,
            window.location.href,
            selectedVideo.id
        );
        const response = await fetch(
            alephURI + '/api-v1/video/' + selectedVideo.id,
            {
                method: 'delete',
            }
        );

        if (response.status === 200) {
            dispatch({
                type: 'ON_DELETE_VIDEO',
                payload: selectedVideo.id,
            });
            setVideoLibraryState(
                compose(
                    Lens.fromProp<IVideoLibraryState>()('selectedVideo').set(
                        {}
                    ),
                    Lens.fromPath<IVideoLibraryState>()([
                        'modalsOpened',
                        'deleteVideoModal',
                    ]).set(false)
                )(videoLibraryState)
            );
        }
    };

    const editVideo = async (form: any) => {
        sendUserEvent(
            gameFootageEditVideo,
            window.location.href,
            selectedVideo.id
        );
        try {
            await fetch(
                process.env.REACT_APP_ALEPH_URI +
                    '/api-v1/video/rename/' +
                    selectedVideo.id,
                {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: form.title,
                        subtitle: form.subtitle,
                    }),
                }
            );
        } catch (error) {
            console.log(error);
        }

        closeModal('editVideoModal');
        window.location.reload();
    };

    const openUploadModal = () => {
        sendUserEvent(gameFootageUploadOpened, window.location.href);
        dispatch({
            type: 'TOGGLE_UPLOAD_MODAL',
            payload: true,
        });
    };

    const closeUploadModal = () => {
        dispatch({
            type: 'TOGGLE_UPLOAD_MODAL',
            payload: false,
        });
    };

    const onVideoUpload = (id: string, title: string) =>
        localStateMgr.dispatch({
            type: 'ON_UPLOAD_START',
            video: {
                id,
                title,
                progress: 0,
            },
        });

    const onUploadProgress = (id: string, percentage: number) =>
        localStateMgr.dispatch({
            type: 'ON_UPLOAD_PROGRESS',
            videoID: id,
            percentage,
        });

    const onStartConversion = (id: string) =>
        localStateMgr.dispatch({
            type: 'ON_START_CONVERSION',
            videoID: id,
        });

    const onVideoUploadComplete = (id: string) => {
        loadVideos('ON_UPLOAD_VIDEO', 1, 0);
        localStateMgr.dispatch({
            type: 'ON_UPLOAD_SUCCESS',
            videoID: id,
        });
    };

    const changeSort = (event: React.ChangeEvent<{ value: Sort }>) => {
        sendUserEvent(gameFootageSort, window.location.href);
        dispatch({
            type: 'CHANGE_SORT',
            payload: event.target.value,
        });
    };

    const getEditVideoFields = () => {
        const fields = editVideoModalFields.map((field: any) => {
            return {
                ...field,
                defaultValue: selectedVideo[field.name] || '',
            };
        });

        return fields;
    };

    const renderValue = () => {
        const selectedSort = sorts.find(
            (sort) =>
                state.sort[0] === sort.value[0] &&
                state.sort[1] === sort.value[1]
        );
        return selectedSort?.name;
    };

    const onScroll = () => {
        if (videoListRef.current) {
            const currentPosition =
                videoListRef.current.scrollHeight -
                videoListRef.current.clientHeight;
            const lastPosition = videoListRef.current.scrollTop;

            if (currentPosition === lastPosition) {
                loadVideos(
                    'ON_LOAD_MORE_VIDEOS',
                    pagination.limit,
                    state.videos.length
                );
            }
        }
    };
    const handleReloginOnABackground = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            try {
                const response = await services.login({
                    username: localStorage.getItem('username'),
                    password: localStorage.getItem('password'),
                    errors: { state: false, serverErrors: '' },
                });
                localStorage.setItem('authToken', response?.data?.authToken);
            } catch (err) {
                console.log(err);
            }
        }
    };
    const handleUpdateAuthToken = async () => {
        return;
        const token = localStorage.getItem('authToken');
        if (token) {
            const updateTokenOpts = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + token,
                },
            };
            const newToken: any = await fetch(
                process.env.REACT_APP_ALEPH_URI +
                    '/api-v1/authentication/renew-token',
                updateTokenOpts
            );
            localStorage.setItem('authToken', newToken.data?.authToken);
        }
    };
    React.useEffect(() => {
        handleReloginOnABackground();
    }, []);
    React.useEffect(() => {
        handleUpdateAuthToken();
    }, []);

    return state.loading ? (
        <Box className={classes.message}>Loading...</Box>
    ) : state.error ? (
        <Box className={classes.message}>Something went wrong.</Box>
    ) : (
        <Box className={classes.container}>
            {/* header secction start */}
            <Box
                className={classes.header}
                sx={{
                    justifyContent: 'flex-end',
                    gridColumnGap: {
                        xs: 10,
                        sm: 25,
                        md: 20,
                    },
                    alignItems: 'center',
                }}
            >
                <Box
                    className={classes.title}
                    style={{ marginRight: 'auto', minWidth: '140px' }}
                >
                    <img
                        src={KfLogo}
                        width='126'
                        style={{ marginTop: '6px', marginBottom: '6px' }}
                    />
                </Box>

                <Box style={{ margin: '0', textTransform: 'capitalize' }}>
                    <a
                        href='https://www.youtube.com/watch?v=Ro99LZSAVB8&list=PLFoXlCcL4SJVBQyXVhrjStxDlghlFoIWp'
                        style={{ color: '#fff', textDecoration: 'none' }}
                        target='_blank'
                    >
                        <Box
                            style={{
                                // height: '40px',
                                width: 'fit-content',
                                display: 'flex',
                                // marginTop: '10px',
                                borderRadius: '20px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 12,
                                height: 40,
                            }}
                            sx={{
                                padding: {
                                    xs: '0 4px',
                                    sm: '0 14px',
                                },
                            }}
                            className={classes.menuHover}
                        >
                            {/* <Box sx={{ margin: 0, padding: 0 }}> */}
                            {/* {' '}
                                <PlayArrowIcon
                                    style={{
                                        margin: '10px',
                                        marginTop: '8px',
                                        marginRight: 'auto',
                                    }}
                                />{' '} */}
                            <PlayArrowIconNew />
                            {/* </Box> */}
                            <Box
                                display={{ xs: 'none', lg: 'block' }}
                                style={{
                                    float: 'right',
                                    textAlign: 'center',
                                    // margin: '10px',
                                }}
                                sx={{ fontWeight: 500 }}
                            >
                                Watch Tutorials
                            </Box>
                        </Box>
                    </a>
                </Box>

                <Box
                    style={{
                        margin: '0px',
                        padding: 0,
                        textTransform: 'capitalize',
                        alignItems: 'center',
                    }}
                >
                    {/* <a
                        href='#'
                        style={{ color: '#fff', textDecoration: 'none' }}
                    > */}
                    <Box
                        style={{
                            width: 'fit-content',
                            display: 'flex',
                            // marginTop: '10px',
                            borderRadius: '20px',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 12,
                            height: 40,
                            cursor: 'pointer',
                        }}
                        sx={{
                            padding: {
                                xs: '0 4px',
                                sm: '0 14px',
                            },
                        }}
                        onClick={openModal('contactUsModal', {} as IClip)}
                    >
                        {/* <Box> */}
                        {/* <img
                                    src={ContactUsIcon}
                                    width='20'
                                    style={{
                                        margin: '0px',
                                        marginRight: 'auto',
                                    }}
                                /> */}
                        <ContactUsIconNew />
                        {/* </Box> */}
                        <Box
                            display={{ xs: 'none', lg: 'block' }}
                            style={{
                                float: 'right',
                                textAlign: 'center',
                                // margin: '10px',
                            }}
                            sx={{ fontWeight: 500 }}
                        >
                            Contact Us
                        </Box>
                    </Box>
                    {/* </a> */}
                </Box>

                {license && (
                    <Box
                        style={{
                            textTransform: 'capitalize',
                        }}
                        sx={{
                            padding: {
                                xs: '0 4px',
                                sm: '0 14px',
                            },
                        }}
                    >
                        <a
                            onClick={() =>
                                window.open(
                                    process.env.REACT_APP_STRIPE_PORTAL + '',
                                    '_blank'
                                )
                            }
                            href='#'
                            style={{
                                textDecoration: 'none',
                                color: '#121217',
                            }}
                        >
                            <Box
                                style={{
                                    height: '40px',
                                    width: 'fit-content',
                                    display: 'flex',
                                    marginTop: 0,
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Box
                                    display={{ xs: 'none', sm: 'flex' }}
                                    style={{
                                        float: 'right',
                                        textAlign: 'center',
                                        alignItems: 'center',
                                        padding: '0 16px',
                                        fontWeight: 500,
                                    }}
                                >
                                    Account
                                </Box>
                                <Box
                                    display={{ xs: 'block', sm: 'none' }}
                                    style={{
                                        float: 'right',
                                        textAlign: 'center',
                                        margin: '10px',
                                    }}
                                >
                                    My Account
                                </Box>
                            </Box>
                        </a>
                    </Box>
                )}

                {!license && (
                    <Box
                        // sx={{ marginLeft: { xs: '17px', sm: '9px' } }}
                        style={{
                            textTransform: 'capitalize',
                        }}
                        sx={{
                            padding: {
                                xs: '0 4px',
                                sm: '0 14px',
                            },
                        }}
                    >
                        <a
                            href='/payments/make-payment'
                            style={{
                                color: '#121217',
                                textDecoration: 'none',
                            }}
                        >
                            <Box
                                style={{
                                    height: '40px',
                                    width: 'fit-content',
                                    display: 'flex',
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    display={{ xs: 'none', sm: 'block' }}
                                    style={{
                                        float: 'right',
                                        textAlign: 'center',
                                        alignItems: 'center',
                                        // margin: '10px',
                                        padding: '0 16px',
                                        fontWeight: 500,
                                    }}
                                >
                                    Buy a License
                                </Box>
                                <Box
                                    display={{ xs: 'block', sm: 'none' }}
                                    style={{
                                        float: 'right',
                                        textAlign: 'center',
                                        margin: '10px',
                                    }}
                                >
                                    Buy
                                </Box>
                            </Box>
                        </a>
                    </Box>
                )}

                <Box
                    style={{ textTransform: 'capitalize' }}
                    sx={{
                        paddingLeft: {
                            xs: '4px',
                            sm: '14px',
                        },
                    }}
                >
                    <a
                        href='/logout'
                        style={{ color: '#fff', textDecoration: 'none' }}
                    >
                        <Box
                            sx={{
                                border: {
                                    xs: 'none',
                                    sm: '1px solid rgba(255, 255, 255, 0.2)',
                                },
                                padding: {
                                    xs: 0,
                                    sm: '0 16px',
                                },
                            }}
                            style={{
                                height: '40px',
                                width: 'fit-content',
                                display: 'flex',
                                borderRadius: '8px',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            {/* <Box> */}
                            {/* <img
                                    src={LogoutIcon}
                                    width='20'
                                    style={{
                                        margin: '10px',
                                        marginRight: 'auto',
                                    }}
                                /> */}
                            <LogoutIconNew />
                            {/* </Box> */}
                            <Box
                                display={{ xs: 'none', lg: 'block' }}
                                style={{
                                    float: 'right',
                                    // margin: '10px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                }}
                            >
                                Log Out
                            </Box>
                        </Box>
                    </a>
                </Box>
            </Box>
            {/* header secction end */}
            <div
                className={classes.scrollBar}
                ref={videoListRef}
                onScroll={onScroll}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        height: 'fit-content',
                    }}
                >
                    {/* upload and filters section start */}
                    <div style={{ width: '100%', display: 'flex' }}>
                        {state.videos.length === 0 ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    left: '50%',
                                }}
                            >
                                <p
                                    style={{
                                        margin: 0,
                                        padding: '0 0 16px',
                                        fontSize: 24,
                                        fontWeight: 500,
                                    }}
                                >
                                    Upload your videos to get started
                                </p>
                                <Button
                                    aria-label='Add'
                                    className={classes.uploadButton}
                                    onClick={openUploadModal}
                                    style={{ margin: 0 }}
                                    startIcon={
                                        <img
                                            src={UploadIcon}
                                            alt='upload video'
                                            width='14'
                                            height='14'
                                        />
                                    }
                                >
                                    {state.uploadVideoButtonText
                                        ? 'Upload Video'
                                        : ''}
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Button
                                    aria-label='Add'
                                    className={classes.uploadButton}
                                    onClick={openUploadModal}
                                    startIcon={
                                        <img
                                            src={UploadIcon}
                                            alt='upload video'
                                            width='14'
                                            height='14'
                                        />
                                    }
                                >
                                    {state.uploadVideoButtonText
                                        ? 'Upload Video'
                                        : ''}
                                </Button>
                                <Box className={classes.sort}>
                                    <FormControl
                                        size='small'
                                        className={classes.formControl}
                                    >
                                        <Select
                                            value={state.sort}
                                            variant='outlined'
                                            renderValue={renderValue}
                                            onChange={changeSort}
                                            placeholder='Sort by'
                                            style={{
                                                borderColor:
                                                    'rgba(255, 255, 255, 0.2)',
                                                borderRadius: 8,
                                            }}
                                            classes={{
                                                icon: classes.selectIcon,
                                                select: classes.select,
                                            }}
                                            IconComponent={DownArrowIcon}
                                        >
                                            {sorts.map((sort, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={sort.value}
                                                    className={classes.menuItem}
                                                >
                                                    {sort.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                    </div>
                    {/* upload and filters section end */}
                    <Grid
                        container={true}
                        spacing={3}
                        style={{
                            marginTop: '16px',
                            marginLeft: '16px',
                            marginRight: '16px',
                        }}
                    >
                        {state.videos.map(
                            (clip: IClip, index: number, videos: IClip[]) => (
                                <VideoTile
                                    key={index}
                                    index={index}
                                    clip={clip}
                                    openModal={openModal}
                                    videos={state.videos}
                                    rootDispatch={dispatch}
                                />
                            )
                        )}
                    </Grid>
                </div>
            </div>
            <VideoUploadDialog
                open={state.uploadModal.open}
                showScreenshot={state.uploadModal.videoUploaded}
                onVideoUpload={onVideoUpload}
                onVideoUploadComplete={onVideoUploadComplete}
                onStartConversion={onStartConversion}
                closeModal={closeUploadModal}
                onUploadProgress={onUploadProgress}
            />
            <ModalDialog
                open={videoLibraryState.modalsOpened.editVideoModal}
                //open={true}
                title={'Edit Clip'}
                fields={getEditVideoFields()}
                onConfirm={editVideo}
                onClose={() => closeModal('editVideoModal')}
                icon={EditIcon}
            />
            <ModalDialog
                open={videoLibraryState.modalsOpened.deleteVideoModal}
                title={'Delete the video'}
                confirmButtonText={'Delete'}
                onConfirm={deleteVideo}
                onClose={() => closeModal('deleteVideoModal')}
                description={'Are you sure you want to delete this clip?'}
                icon={DeleteIcon}
            />
            <ModalDialog
                open={videoLibraryState.modalsOpened.contactUsModal}
                //open={true}
                title={'Contact Us'}
                confirmButtonText={'Delete'}
                // onConfirm={deleteVideo}
                onClose={() => closeModal('contactUsModal')}
                icon={ContactIcon}
                showDefaultButton={false}
                contactForm={true}
            />
        </Box>
    );
};

interface IProps {
    isUserSubscribed: boolean;
    hasLicense: boolean;
}

const EnhancedVideoList = compose(
    withStyles(styles),
    withNavigation,
    withVideoLibraryState,
    withApollo,
    withLocalState
)(VideoList);

export const VideoListView = ({ hasLicense }: IProps) => {
    license = hasLicense;
    return <EnhancedVideoList />;
};
